import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileOpenCategory, setMobileOpenCategory] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === '/';
  const isTransparent = isHome && !isScrolled;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isHome) {
      navigate('/');
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const textColor = isTransparent ? 'text-white' : 'text-foreground-900';
  const textHover = isTransparent ? 'hover:text-white/80 hover:bg-white/10' : 'hover:text-foreground-900 hover:bg-background-100';
  const mutedText = isTransparent ? 'text-white/80' : 'text-foreground-700';
  const activeTab = isTransparent
    ? 'text-white bg-white/15'
    : 'text-primary-600 bg-primary-100/70';
  const logoColor = isTransparent ? 'text-white' : 'text-foreground-900';
  const navBg = isTransparent
    ? 'bg-transparent'
    : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}
      style={isTransparent ? { textShadow: '0 1px 3px rgba(0,0,0,0.3)' } : {}}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            onClick={handleLogoClick}
            className="flex-shrink-0 cursor-pointer"
          >
            <span className={`font-heading text-xl md:text-2xl font-bold whitespace-nowrap ${logoColor}`}>
              Hoobuy<span className={isTransparent ? 'text-white' : 'text-primary-500'}>Spreadsheet</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              onClick={handleLogoClick}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                isHome ? activeTab : `${mutedText} ${textHover}`
              }`}
            >
              {t('nav.home')}
            </a>

            <a
              href="/guia-de-compras"
              onClick={(e) => {
                e.preventDefault();
                navigate('/guia-de-compras');
              }}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                location.pathname === '/guia-de-compras' ? activeTab : `${mutedText} ${textHover}`
              }`}
            >
              {t('nav.shoppingGuide')}
            </a>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                  isDropdownOpen ? activeTab : `${mutedText} ${textHover}`
                }`}
              >
                {t('nav.categories')}
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-[720px] bg-background-50 rounded-xl border border-background-200/70 shadow-lg animate-scale-in overflow-hidden">
                  <div className="grid grid-cols-3 gap-1 p-3">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent-50/80 transition-colors group cursor-pointer"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-background-100">
                          <img
                            src={cat.image}
                            alt={cat.namePt}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground-900 group-hover:text-accent-700 truncate whitespace-nowrap">
                            {cat.namePt}
                          </p>
                          <p className="text-xs text-foreground-500 truncate whitespace-nowrap">
                            {cat.name}
                          </p>
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                isTransparent
                  ? 'bg-white/20 text-white border border-white/30 hover:bg-white/30'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              {t('nav.sheet')}
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
                isTransparent
                  ? 'bg-white text-foreground-900 hover:bg-white/90'
                  : 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
              }`}
            >
              buy it now
              <i className="ri-shopping-cart-line text-sm"></i>
            </a>
          </div>

          <button
            className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg cursor-pointer"
            style={isTransparent ? { color: '#fff', textShadow: '0 1px 3px rgba(0,0,0,0.3)' } : {}}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/60 max-h-[85vh] overflow-y-auto animate-scale-in">
          <div className="px-4 py-4 space-y-1">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                navigate('/');
                setIsMobileMenuOpen(false);
              }}
              className={`block px-4 py-3 text-sm font-semibold rounded-lg cursor-pointer ${
                isHome ? 'text-primary-600 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              <i className="ri-home-4-line mr-2"></i>
              {t('nav.home')}
            </a>

            <a
              href="/guia-de-compras"
              onClick={(e) => {
                e.preventDefault();
                navigate('/guia-de-compras');
                setIsMobileMenuOpen(false);
              }}
              className={`block px-4 py-3 text-sm font-semibold rounded-lg cursor-pointer ${
                location.pathname === '/guia-de-compras' ? 'text-primary-600 bg-primary-50' : 'text-foreground-700'
              }`}
            >
              <i className="ri-book-open-line mr-2"></i>
              {t('nav.shoppingGuide')}
            </a>

            <div className="pt-2">
              <button
                onClick={() => setMobileOpenCategory(mobileOpenCategory === 'categories' ? null : 'categories')}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-700 rounded-lg cursor-pointer"
              >
                <span>
                  <i className="ri-grid-line mr-2"></i>
                  {t('nav.categories')}
                </span>
                <i className={`ri-arrow-down-s-line transition-transform ${mobileOpenCategory === 'categories' ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileOpenCategory === 'categories' && (
                <div className="grid grid-cols-2 gap-1 px-2 pt-1 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-accent-50/80 transition-colors cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                        <img
                          src={cat.image}
                          alt={cat.namePt}
                          className="w-full h-full object-cover object-top"
                          loading="lazy"
                        />
                      </div>
                      <span className="text-sm text-foreground-700">{cat.namePt}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2 flex flex-col gap-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block px-4 py-3 text-sm font-semibold text-white bg-primary-500 rounded-lg cursor-pointer text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.sheet')}
                <i className="ri-arrow-right-up-line ml-1.5"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block px-4 py-3 text-sm font-semibold text-foreground-900 bg-background-200 rounded-lg cursor-pointer text-center"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                buy it now
                <i className="ri-shopping-cart-line ml-1.5"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}